import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { authApi } from "../lib/api";

const AuthContext = createContext(null);

const isAdminPath = () => typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
// Returning from Google sign-in: the URL fragment carries a one-time session_id that AuthCallback exchanges first.
const hasSessionFragment = () => typeof window !== "undefined" && window.location.hash.includes("session_id=");

/**
 * Admin session state. The session itself lives in an HttpOnly cookie managed by the backend,
 * so we ask the API who we are instead of reading anything from browser storage.
 * The check only runs for /admin routes so public visitors never hit the auth endpoint.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(null);

  const refresh = useCallback(() => {
    if (inFlight.current) return inFlight.current;
    setLoading(true);
    inFlight.current = authApi
      .me()
      .then((me) => {
        setUser(me);
        return me;
      })
      .catch(() => {
        setUser(null); // not signed in - expected
        return null;
      })
      .finally(() => {
        setChecked(true);
        setLoading(false);
        inFlight.current = null;
      });
    return inFlight.current;
  }, []);

  useEffect(() => {
    if (isAdminPath() && !hasSessionFragment()) refresh();
    const onUnauthorized = () => setUser(null);
    window.addEventListener("avalon:unauthorized", onUnauthorized);
    return () => window.removeEventListener("avalon:unauthorized", onUnauthorized);
  }, [refresh]);

  const login = useCallback(async (username, password) => {
    const data = await authApi.login(username, password);
    setUser(data.user);
    setChecked(true);
    return data.user;
  }, []);

  const loginWithGoogleSession = useCallback(async (sessionId) => {
    const data = await authApi.googleSession(sessionId);
    setUser(data.user);
    setChecked(true);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.warn("Logout request failed; clearing local session anyway", err);
    }
    setUser(null);
  }, []);

  const actions = useMemo(() => ({ refresh, login, loginWithGoogleSession, logout }), [refresh, login, loginWithGoogleSession, logout]);
  const value = useMemo(
    () => ({ user, loading, checked, ...actions, isAuthenticated: Boolean(user), isMaster: user?.role === "master" }),
    [user, loading, checked, actions]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
